import { useEffect, useState } from 'react';

/* A deploy does not reach a tab that is already open: the SPA keeps running the
   bundle it loaded, so a rep who left clientIQ open since last week sees last
   week's screens (the Text and Email tabs "missing" for some people was this).
   Every production build has a new hashed entry script, so on focus, at most
   every few minutes, index.html is fetched afresh and its entry compared with
   the one this page loaded; a difference means a newer build and the shell
   offers a reload. In dev the entry is /src/main.tsx, so nothing is compared. */
const CHECK_EVERY = 5 * 60_000;

const entryOf = (html: string): string | null =>
  /<script[^>]*\btype="module"[^>]*\bsrc="([^"]*\/assets\/[^"]+)"/.exec(html)?.[1]
  ?? /<script[^>]*\bsrc="([^"]*\/assets\/[^"]+)"[^>]*\btype="module"/.exec(html)?.[1]
  ?? null;

export function useUpdateAvailable(): boolean {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    const current = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src');
    if (!current) return;
    let last = Date.now(); // this page just loaded the current build
    let cancelled = false;
    const check = async () => {
      if (cancelled || document.visibilityState !== 'visible' || Date.now() - last < CHECK_EVERY) return;
      last = Date.now();
      try {
        const res = await fetch(`/?u=${last}`, { cache: 'no-store', headers: { accept: 'text/html' } });
        if (!res.ok) return;
        const next = entryOf(await res.text());
        if (!cancelled && next && next !== current) setStale(true);
      } catch { /* offline or mid-deploy: the next focus tries again */ }
    };
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    const timer = window.setInterval(() => void check(), CHECK_EVERY);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.clearInterval(timer);
    };
  }, []);
  return stale;
}
